import React, { FunctionComponent, useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'
import { get } from 'lodash'

import {
  DateRangeFilter,
  FilterBox,
  GenericInput,
  Select,
} from '../../../components'
import { PerformanceTableContext } from './index'

import { State } from '../../../reducers'
import { getLinks } from '../../../actions/links'
import { getProductTypes } from '../../../actions/productTypes'
import { getCategories } from '../../../actions/categories'
import { getAdvertisers } from '../../../actions/advertisers'
import { LinksStore } from '../../../typings/stores/LinksStore'
import { ProductTypesStore } from '../../../typings/stores/ProductTypesStore'
import { AdvertisersStore } from '../../../typings/stores/AdvertiserStore'
import { CategoriesStore } from '../../../typings/stores/CategoriesStore'

import styles from './filters.module.css'

const PerformanceFilters: FunctionComponent = () => {
  const dispatch = useDispatch()
  const { setFilter, setFilters, filters } = useContext(PerformanceTableContext)
  const productTypes = useSelector<State, ProductTypesStore>(
    state => state.productTypes
  )
  const products = useSelector<State, LinksStore>(state => state.links)
  const categories = useSelector<State, CategoriesStore>(
    state => state.categories
  )
  const { preloaded: advertisers } = useSelector<State, AdvertisersStore>(
    state => state.advertisers
  )

  // TODO this might want to be done way further up, as its used in a few places
  useEffect(() => {
    dispatch(getLinks())
    dispatch(getProductTypes())
    dispatch(getCategories())
    dispatch(getAdvertisers())
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <FilterBox label="Date type">
          <div className={styles.dateTypeContainer}>
            <button
              className={classnames(styles.toggleBtn, {
                [styles.selected]: filters.dateType === 'clickDate',
              })}
              onClick={() => setFilter('dateType', 'clickDate')}
            >
              Click Date
            </button>
            <button
              className={classnames(styles.toggleBtn, {
                [styles.selected]: filters.dateType === 'processDate',
              })}
              onClick={() => setFilter('dateType', 'processDate')}
            >
              Process Date
            </button>
          </div>
        </FilterBox>
        <DateRangeFilter
          onChange={setFilters}
          dates={{
            startDate: filters.startDate,
            endDate: filters.endDate,
            dateRange: filters.dateRange,
          }}
        />
        <FilterBox label="Group by" className={styles.groupBy}>
          <div className={styles.groupContainer}>
            <button
              className={classnames(styles.toggleBtn, {
                [styles.selected]: filters.groupBy === 'productType',
              })}
              onClick={() => setFilter('groupBy', 'productType')}
            >
              Product Type
            </button>
            <button
              className={classnames(styles.toggleBtn, {
                [styles.selected]: filters.groupBy === 'product',
              })}
              onClick={() => setFilter('groupBy', 'product')}
            >
              Product
            </button>
            <button
              className={classnames(styles.toggleBtn, {
                [styles.selected]: filters.groupBy === 'advertiser',
              })}
              onClick={() => setFilter('groupBy', 'advertiser')}
            >
              Advertiser
            </button>
            <button
              className={classnames(styles.toggleBtn, {
                [styles.selected]: filters.groupBy === 'category',
              })}
              onClick={() => setFilter('groupBy', 'category')}
            >
              Category
            </button>
            <button
              className={classnames(styles.toggleBtn, {
                [styles.selected]: filters.groupBy === 'subtracking',
              })}
              onClick={() => setFilter('groupBy', 'subtracking')}
            >
              Subtracking
            </button>
          </div>
        </FilterBox>
      </div>
      <div className={styles.row}>
        <FilterBox label="Filter by" className={styles.filterBy}>
          <div className={styles.filterByContainer}>
            <Select
              className={styles.dropdown}
              placeholder="All Product Types"
              loadingMessage="Loading product types"
              isLoading={productTypes.fetching}
              selected={get(filters, 'productType', '')}
              options={productTypes.data.map(pt => ({
                value: pt.id,
                text: pt.name,
              }))}
              onChange={e => setFilter('productType', e.currentTarget.value)}
            />
            <Select
              className={styles.dropdown}
              placeholder="All Products"
              loadingMessage="Loading products"
              isLoading={products.status === 'fetching'}
              selected={get(filters, 'product', '')}
              options={products.preloaded.map(p => ({
                text: p.name,
                value: p.id,
              }))}
              onChange={e => setFilter('product', e.currentTarget.value)}
            />
            <Select
              className={styles.dropdown}
              placeholder="All Advertisers"
              loadingMessage="Loading advertisers"
              isLoading={advertisers.length === 0}
              selected={get(filters, 'advertiser', '')}
              options={advertisers.map(a => ({
                value: a.id.toString(),
                text: a.name,
              }))}
              onChange={e => setFilter('advertiser', e.currentTarget.value)}
            />
            <Select
              className={styles.dropdown}
              placeholder="All Categories"
              loadingMessage="Loading categories"
              isLoading={categories.fetching}
              selected={get(filters, 'category', '')}
              options={categories.data.map(c => ({
                value: c.id,
                text: c.name,
              }))}
              onChange={e => setFilter('category', e.currentTarget.value)}
            />
            <GenericInput
              defaultValue={filters.subtracking}
              placeholder="subtracking"
              onChange={e =>
                setFilter('subtracking', e.target.value, {
                  routeUpdate: 'replace',
                })
              }
            />
          </div>
        </FilterBox>
      </div>
    </div>
  )
}

export default PerformanceFilters
